import { useState, useEffect } from "react";
import { Button } from "@/shared/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/shared/components/ui/card";
import { Badge } from "@/shared/components/ui/badge";
import { useToast } from "@/shared/hooks/use-toast";
import { apiClient } from "@/shared/services/apiClient";
import { API_ROUTES } from "@/shared/services/apiRoutes";
import { Calendar, CheckCircle, XCircle, Loader2 } from "lucide-react";

export default function GoogleCalendarIntegration() {
  const { toast } = useToast();
  const [connected, setConnected] = useState(false);
  const [connectedEmail, setConnectedEmail] = useState<string | undefined>();
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  // Load status on mount
  useEffect(() => {
    fetchStatus();

    // If we just came back from OAuth, handle the code in the URL
    const params = new URLSearchParams(window.location.search);
    const code = params.get('gcal_code');
    if (code) {
      // Clear the code from the URL
      const newUrl = window.location.pathname + window.location.hash;
      window.history.replaceState({}, '', newUrl);
      exchangeCode(code);
    }
  }, []);

  async function fetchStatus() {
    try {
      const data = await apiClient.get<{ ok: boolean; connected: boolean; email?: string }>(
        API_ROUTES.GOOGLE_CALENDAR_STATUS
      );
      setConnected(data.connected);
      setConnectedEmail(data.email);
    } catch (err) {
      console.error('[GoogleCalendar] Status fetch failed:', err);
    } finally {
      setLoading(false);
    }
  }

  async function exchangeCode(code: string) {
    setConnecting(true);
    try {
      const redirectUri = `${window.location.origin}/auth/google-calendar/callback`;
      const data = await apiClient.post<{ ok: boolean; email: string }>(
        API_ROUTES.GOOGLE_CALENDAR_CONNECT,
        { code, redirectUri }
      );
      setConnected(true);
      setConnectedEmail(data.email);
      toast({ title: "Google Calendar connected!", description: `Syncing to ${data.email}` });
    } catch (err: any) {
      toast({ title: "Connection failed", description: err.message, variant: "destructive" });
    } finally {
      setConnecting(false);
    }
  }

  async function handleConnect() {
    setConnecting(true);
    try {
      const redirectUri = `${window.location.origin}/auth/google-calendar/callback`;
      const data = await apiClient.get<{ ok: boolean; authUrl: string }>(
        `${API_ROUTES.GOOGLE_CALENDAR_AUTH_URL}?redirectUri=${encodeURIComponent(redirectUri)}`
      );
      // Redirect to Google OAuth
      window.location.href = data.authUrl;
    } catch (err: any) {
      toast({ title: "Failed to start connection", description: err.message, variant: "destructive" });
      setConnecting(false);
    }
  }

  async function handleDisconnect() {
    setDisconnecting(true);
    try {
      await apiClient.post(API_ROUTES.GOOGLE_CALENDAR_DISCONNECT, {});
      setConnected(false);
      setConnectedEmail(undefined);
      toast({ title: "Google Calendar disconnected" });
    } catch (err: any) {
      toast({ title: "Disconnect failed", description: err.message, variant: "destructive" });
    } finally {
      setDisconnecting(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50 dark:bg-blue-950">
            <Calendar className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <CardTitle className="text-base">Google Calendar</CardTitle>
            <CardDescription>
              Automatically sync your scheduled calls to Google Calendar
            </CardDescription>
          </div>
          {!loading && (
            <Badge
              variant={connected ? "default" : "secondary"}
              className="ml-auto"
            >
              {connected ? (
                <><CheckCircle className="mr-1 h-3 w-3" /> Connected</>
              ) : (
                <><XCircle className="mr-1 h-3 w-3" /> Not connected</>
              )}
            </Badge>
          )}
        </div>
      </CardHeader>

      <CardContent>
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Checking connection status...
          </div>
        ) : connected ? (
          <div className="space-y-3">
            {connectedEmail && (
              <p className="text-sm text-muted-foreground">
                Syncing to <span className="font-medium text-foreground">{connectedEmail}</span>
              </p>
            )}
            <p className="text-sm text-muted-foreground">
              New, updated, and deleted events in wlkthru will automatically sync to your Google Calendar.
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={handleDisconnect}
              disabled={disconnecting}
            >
              {disconnecting ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Disconnecting...</>
              ) : (
                "Disconnect"
              )}
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Connect your Google Calendar to automatically sync scheduled calls. You&apos;ll be asked to authorize calendar access.
            </p>
            <Button
              onClick={handleConnect}
              disabled={connecting}
              size="sm"
            >
              {connecting ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Connecting...</>
              ) : (
                <>
                  <Calendar className="mr-2 h-4 w-4" />
                  Connect Google Calendar
                </>
              )}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
